import React, { useEffect } from "react";
import Menu from "../component/menu/Menu";
import Footer from "./Footer";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl } from "./BaseURL/BaseURL";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trackInitiateCheckout } from "../lib/fbPixel";

function ViewCart() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isAuthenticated = !!localStorage.getItem("token");
  const [guestCartState, setGuestCartState] = React.useState(() => JSON.parse(localStorage.getItem("guestCart")) || []);

  // Queries
  const {
    data: cartData = [],
    isLoading: cartLoading,
    isError: cartError,
  } = useQuery({
    queryKey: ["cart"],
    queryFn: async () => {
      const res = await axios.get(`${baseurl}/get-cart`, {
        headers: authHeaders(),
      });
      return res.data.data;
    },
  });

  // Base subtotal without delivery
  const {
    data: baseSubtotal = 0,
    isLoading: baseLoading,
    isError: baseError,
  } = useQuery({
    queryKey: ["baseSubtotal"],
    queryFn: async () => {
      const res = await axios.get(`${baseurl}/get-subtotals`, {
        headers: authHeaders(),
      });
      return res.data?.data?.subtotal ?? 0;
    },
  });

  console.log("here it is", baseSubtotal)

  // Prefer computing subtotal locally from cart data to avoid inconsistent API responses
  const baseFromCart = isAuthenticated
    ? (cartLoading ? 0 : (cartData || []).reduce((s, it) => s + (it.subtotal || 0), 0))
    : guestCartState.reduce((s, it) => s + (it.subtotal || 0), 0);

  const deliveryCharge = (isAuthenticated ? !cartLoading && !cartError : true)
    ? (baseFromCart >= 3000 ? 0 : 200)
    : 0;

  const computedTotal = (isAuthenticated ? !cartLoading && !cartError : true)
    ? baseFromCart + deliveryCharge
    : 0;

  // Persist total to localStorage for checkout flow (works for both auth and guest)
  useEffect(() => {
    if (isAuthenticated) {
      if (!cartLoading && !cartError) {
        localStorage.setItem("subtotal", String(computedTotal));
      }
    } else {
      // guest
      localStorage.setItem("subtotal", String(computedTotal));
    }
  }, [isAuthenticated, cartLoading, cartError, computedTotal]);

  // Mutations
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ itemId, quantity }) => {
      const res = await axios.patch(
        `${baseurl}/cart/${itemId}`,
        { cartQuantity: quantity },
        { headers: authHeaders() }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      queryClient.invalidateQueries({ queryKey: ["baseSubtotal"] });
      queryClient.invalidateQueries({ queryKey: ["totalSubtotal"] });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId) => {
      const res = await axios.delete(`${baseurl}/delete-item/${itemId}`, {
        headers: authHeaders(),
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      queryClient.invalidateQueries({ queryKey: ["baseSubtotal"] });
      queryClient.invalidateQueries({ queryKey: ["totalSubtotal"] });
    },
  });

  const increment = (item) => {
    if (isAuthenticated) {
      const newQty = (item?.cartQuantity || 0) + 1;
      updateQuantityMutation.mutate({ itemId: item._id, quantity: newQty });
    } else {
      const updated = guestCartState.map((it) =>
        it.productId === item.productId ? { ...it, quantity: it.quantity + 1, subtotal: (it.quantity + 1) * it.price } : it
      );
      localStorage.setItem("guestCart", JSON.stringify(updated));
      setGuestCartState(updated);
    }
  };

  const decrement = (item) => {
    if (isAuthenticated) {
      const current = item?.cartQuantity || 0;
      if (current > 0) {
        updateQuantityMutation.mutate({ itemId: item._id, quantity: current - 1 });
      }
    } else {
      const updated = guestCartState.map((it) =>
        it.productId === item.productId && it.quantity > 1 ? { ...it, quantity: it.quantity - 1, subtotal: (it.quantity - 1) * it.price } : it
      );
      localStorage.setItem("guestCart", JSON.stringify(updated));
      setGuestCartState(updated);
    }
  };

  const deleteCartItem = (itemId) => {
    if (isAuthenticated) {
      deleteItemMutation.mutate(itemId);
    } else {
      const updated = guestCartState.filter((it) => it.productId !== itemId);
      localStorage.setItem("guestCart", JSON.stringify(updated));
      setGuestCartState(updated);
    }
  };

  const handleCheckout = () => {
    // If authenticated, use backend cart data; otherwise use guestCartState
    if (isAuthenticated) {
      const productIds = cartData?.map((item) => item?.product_id?._id);
      localStorage.setItem("checkoutProductIds", JSON.stringify(productIds));
    } else {
      const productIds = guestCartState.map((it) => it.productId);
      localStorage.setItem("checkoutProductIds", JSON.stringify(productIds));
      const base = guestCartState.reduce((s, it) => s + it.subtotal, 0);
      const delivery = base >= 3000 ? 0 : 200;
      localStorage.setItem("subtotal", String(base + delivery));
    }

    // Track InitiateCheckout with Meta Pixel
    try {
      const productIds = isAuthenticated
        ? cartData?.map((item) => item?.product_id?._id)
        : guestCartState.map((it) => it.productId);
      trackInitiateCheckout({
        content_ids: productIds || [],
        content_type: "product",
        num_items: productIds?.length || 0,
        value: computedTotal,
        currency: "PKR",
      });
    } catch (e) {
      console.error("Failed to track InitiateCheckout", e);
    }
    // Navigate to guest checkout route when not authenticated
    navigate(isAuthenticated ? "/checkout" : "/guest-checkout");
  };

  // When guest cart changes, persist computed subtotal for checkout
  React.useEffect(() => {
    if (!isAuthenticated) {
      const base = guestCartState.reduce((s, it) => s + it.subtotal, 0);
      const delivery = base >= 3000 ? 0 : 200;
      localStorage.setItem("subtotal", String(base + delivery));
    }
  }, [guestCartState, isAuthenticated]);

  // Build rows for the cart table to avoid complex nested JSX ternaries
  const cartRows = (() => {
    if (isAuthenticated) {
      if (cartLoading) {
        return [
          <tr key="loading">
            <td className="py-3 border px-3">Loading...</td>
            <td className="py-3 border px-3">-</td>
            <td className="py-3 border px-3">-</td>
            <td className="py-3 border px-3">-</td>
            <td className="py-3 border px-3">-</td>
          </tr>,
        ];
      }
      if (cartError) {
        return [
          <tr key="error">
            <td className="py-3 border px-3 text-red-600">Failed to load cart</td>
          </tr>,
        ];
      }

      return cartData?.map((item, index) => (
        <tr key={index} className="w-full">
          <td className="py-3 border md:px-3 sm:flex gap-x-4">
            <img src={item.url} className="sm:w-14 sm:h-16 w-16 h-12 rounded-lg" alt={`Product ${index + 1}`} />
            <h1 className="font-bold pt-3 text-xs ">{item.title}</h1>
          </td>
          <td className="md:py-3 border px-4">{item.price}</td>
          <td className="md:py-3 border px-4">
            <div className="flex">
              <h1 className="border cursor-pointer px-2 py-1 font-semibold" onClick={() => decrement(item)}>-</h1>
              <h1 className="border px-2 py-1">{item.cartQuantity}</h1>
              <h1 className="border cursor-pointer px-2 py-1" onClick={() => increment(item)}>+</h1>
            </div>
          </td>
          <td className="md:py-3 border items-center text-center">{item.subtotal}</td>
          <td className="items-center text-center border px-2 text-xl">
            <button onClick={() => deleteCartItem(item._id)}>
              <RiDeleteBin5Line />
            </button>
          </td>
        </tr>
      ));
    }

    // guest
    if (guestCartState?.length) {
      return guestCartState.map((item, index) => (
        <tr key={index} className="w-full">
          <td className="py-3 border md:px-3 sm:flex gap-x-4">
            <img src={item.url} className="sm:w-14 sm:h-16 w-16 h-12 rounded-lg" alt={`Product ${index + 1}`} />
            <h1 className="font-bold pt-3 text-xs ">{item.title}</h1>
          </td>
          <td className="md:py-3 border px-4">{item.price}</td>
          <td className="md:py-3 border px-4">
            <div className="flex">
              <h1 className="border cursor-pointer px-2 py-1 font-semibold" onClick={() => decrement(item)}>-</h1>
              <h1 className="border px-2 py-1">{item.quantity}</h1>
              <h1 className="border cursor-pointer px-2 py-1" onClick={() => increment(item)}>+</h1>
            </div>
          </td>
          <td className="md:py-3 border items-center text-center">{item.subtotal}</td>
          <td className="items-center text-center border px-2 text-xl">
            <button onClick={() => deleteCartItem(item.productId)}>
              <RiDeleteBin5Line />
            </button>
          </td>
        </tr>
      ));
    }

    return [
      <tr key="empty">
        <td className="py-3 border px-3">Your cart is empty</td>
      </tr>,
    ];
  })();

  return (
    <div>
      <Menu />

      <div className="flex justify-center items-center py-3">
        <h1 className="font-bold text-xl px-8 text-red-600 ">
          IF CHARGES EXCEED RS 3000 DELIVERY WILL BE FREE
        </h1>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:gap-x-10 sm:px-32">
        <div className="border h-80 overflow-hidden overflow-x-auto hover:overflow-y-auto border-black">
          <table>
            <thead>
              <tr className="border">
                <th className="md:py-2 lg:px-5 border border-r border-black">
                  Products
                </th>
                <th className="md:py-2 lg:px-5 border border-r border-black">
                  Price
                </th>
                <th className="md:py-2 lg:px-5 border border-r border-black">
                  Quantity
                </th>
                <th className="md:py-2 lg:px-5 border border-r border-black">
                  Subtotal
                </th>
                <th className="md:py-2 lg:px-5 border border-r border-black">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {cartRows}
            </tbody>
          </table>
        </div>

        <div className="border mt-6 md:mt-0 border-black md:w-64">
          <h1 className="text-xl font-bold py-4 text-center border border-black">
            Total Cart
          </h1>
          <div className="flex justify-between px-5 py-4 font-semibold border-b">
            <h1>Subtotal</h1>
            <h1>
              {isAuthenticated
                ? (cartLoading ? "..." : cartError ? "-" : `${baseFromCart} Rs`)
                : `${baseFromCart} Rs`}
            </h1>
          </div>

          {/* <div className='flex justify-between px-5 py-4 font-semibold border-b'>
            <h1>Tax</h1>
            <h1>free</h1>
          </div> */}

          <div className="flex justify-between px-5 py-4 font-semibold border-b">
            <h1>Delivery charges</h1>
            <h1>
              {isAuthenticated
                ? (cartLoading ? "..." : cartError ? "-" : (baseFromCart >= 3000 ? "Free" : "Rs 200"))
                : (baseFromCart >= 3000 ? "Free" : "Rs 200")}
            </h1>
          </div>

          <div className="flex justify-between px-5 py-4 font-semibold ">
            <h1>Total</h1>
            <h1>
              {isAuthenticated
                ? (cartLoading ? "..." : cartError ? "-" : `${computedTotal} Rs`)
                : `${computedTotal} Rs`}
            </h1>
          </div>
          <button
            className="py-2 w-full rounded-lg bg-[#f4b230]"
            onClick={handleCheckout} // Call handleCheckout function on button click
          >
            CHECK OUT
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ViewCart;
